<template>
  <div class="games-page">
    <!-- 面包屑导航 -->
    <div class="breadcrumb-section">
      <div class="container mx-auto px-4 py-4">
        <nav class="flex" aria-label="Breadcrumb">
          <ol class="inline-flex items-center space-x-1 md:space-x-3">
            <li class="inline-flex items-center">
              <a href="/" class="inline-flex items-center text-sm font-medium text-gray-400 hover:text-white">
                <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
                </svg>
                Home
              </a>
            </li>
            <li>
              <div class="flex items-center">
                <svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span class="ml-1 text-sm font-medium text-white md:ml-2">Games</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>
    </div>
    
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container mx-auto px-4 py-8">
        <h1 class="text-4xl font-bold text-white mb-4">Game Library</h1>
        <p class="text-gray-300 text-lg">Explore our extensive collection of games and find your next favorite</p>
      </div>
    </div>

    <!-- 搜索和筛选区域 -->
    <div class="search-section bg-gray-800">
      <div class="container mx-auto px-4 py-6">
        <div class="flex flex-col lg:flex-row gap-4">
          <!-- 搜索框 -->
          <div class="flex-1">
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search games..."
                class="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                @input="handleSearch"
              />
              <svg class="absolute right-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
          </div>

          <!-- 分类筛选 -->
          <div class="lg:w-64">
            <select
              v-model="selectedCategory"
              @change="handleCategoryChange"
              class="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="">All Categories</option>
              <option v-for="category in categories" :key="category" :value="category">
                {{ category }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- 游戏列表 -->
    <div class="games-section">
      <div class="container mx-auto px-4 py-8">
        <!-- 结果统计 -->
        <div class="mb-6">
          <p class="text-gray-300">
            Found {{ filteredGames.length }} games
            <span v-if="searchQuery || selectedCategory" class="text-blue-400">
              (filtered)
            </span>
          </p>
        </div>

        <!-- 游戏网格 -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div
            v-for="game in filteredGames"
            :key="game.name"
            class="game-card bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105"
            @click="viewGame(game)"
          >
              <!-- 游戏图片 -->
              <div class="game-image relative">
                <img
                  :src="game.thumb6 || game.thumb5 || game.thumb4"
                  :alt="game.name"
                  class="w-full h-48 object-cover"
                  @error="handleImageError"
                />
                <div class="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300"></div>
                <div class="absolute top-2 left-2">
                  <span class="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                    {{ game.category }}
                  </span>
                </div>
              </div>

              <!-- 游戏信息 -->
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 line-clamp-2">{{ game.name }}</h3>
                <p class="text-gray-400 text-sm mb-3 line-clamp-3">{{ game.description }}</p>
                
                <!-- 发布日期 -->
                <div class="text-gray-500 text-xs mb-3">
                  Released: {{ formatDate(game.create_date) }}
                </div>

                <!-- 操作按钮 -->
                <button
                  class="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                  @click.stop="playGame(game)"
                >
                  Play Now
                </button>
              </div>
          </div>
        </div>

        <!-- 无结果提示 -->
        <div v-if="filteredGames.length === 0" class="text-center py-12">
          <div class="text-gray-400 text-lg mb-4">
            <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33"></path>
            </svg>
            No matching games found
          </div>
          <button
            @click="clearFilters"
            class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg transition-colors duration-200"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import gameService from '@/services/gameService'
import analyticsService from '@/services/analyticsService'
import StructuredDataUtils from '@/utils/structuredDataUtils'

export default {
  name: 'Games',
  setup() {
    const router = useRouter()
    const games = ref([])
    const categories = ref([])
    const searchQuery = ref('')
    const selectedCategory = ref('')
    const filteredGames = ref([])

    // 格式化日期
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    // 获取游戏数据
    const loadGames = () => {
      games.value = gameService.getAllGames()
      categories.value = gameService.getGameCategories()
      filteredGames.value = games.value
      
      // 添加游戏集合结构化数据
      if (games.value.length > 0) {
        addGameCollectionStructuredData(games.value)
      }
    }

    // 处理搜索
    const handleSearch = () => {
      filterGames()
      // 跟踪搜索事件
      if (searchQuery.value.trim()) {
        analyticsService.trackSearch(searchQuery.value.trim(), filteredGames.value.length)
      }
    }

    // 处理分类变化
    const handleCategoryChange = () => {
      filterGames()
      // 跟踪分类筛选事件
      if (selectedCategory.value) {
        analyticsService.trackCategoryFilter(selectedCategory.value)
      }
    }

    // 筛选游戏
    const filterGames = () => {
      let filtered = games.value

      // 按搜索词筛选
      if (searchQuery.value.trim()) {
        filtered = gameService.searchGames(searchQuery.value.trim())
      }

      // 按分类筛选
      if (selectedCategory.value) {
        filtered = filtered.filter(game => 
          game.category && game.category.toLowerCase() === selectedCategory.value.toLowerCase()
        )
      }

      filteredGames.value = filtered
    }

    // 清除筛选
    const clearFilters = () => {
      searchQuery.value = ''
      selectedCategory.value = ''
      filteredGames.value = games.value
    }

    // 查看游戏详情
    const viewGame = (game) => {
      // 跳转到游戏详情页
      const gameId = encodeURIComponent(game.name)
      console.log('Navigating to game:', game.name, 'URL:', `/game/${gameId}`)
      // 跟踪游戏点击事件
      analyticsService.trackCustomEvent('game_click', {
        game_name: game.name,
        game_category: game.category,
        event_category: 'Games'
      })
      router.push(`/game/${gameId}`)
    }

    // 开始游戏
    const playGame = (game) => {
      // 跳转到游戏页面
      const gameId = encodeURIComponent(game.name)
      console.log('Playing game:', game.name, 'URL:', `/game/${gameId}`)
      router.push(`/game/${gameId}`)
    }

    // 处理图片加载错误
    const handleImageError = (event) => {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMTAxMDEwIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2NjY2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5HYW1lIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'
    }

    // 添加游戏集合结构化数据
    const addGameCollectionStructuredData = (games) => {
      try {
        const structuredData = StructuredDataUtils.generateGameCollectionStructuredData(games)
        if (structuredData) {
          StructuredDataUtils.addStructuredDataToPage(structuredData, 'games-collection')
          console.log('Games collection structured data added successfully')
        }
      } catch (error) {
        console.error('Error adding games collection structured data:', error)
      }
    }

    onMounted(() => {
      loadGames()
    })

    return {
      games,
      categories,
      searchQuery,
      selectedCategory,
      filteredGames,
      formatDate,
      handleSearch,
      handleCategoryChange,
      clearFilters,
      viewGame,
      playGame,
      handleImageError
    }
  }
}
</script>

<style scoped>
.games-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.page-header {
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.9) 0%, rgba(22, 33, 62, 0.9) 100%);
  backdrop-filter: blur(10px);
}

.search-section {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.game-card {
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.game-card:hover {
  border-color: rgba(59, 130, 246, 0.5);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .games-page {
    padding-bottom: 80px;
  }
}
</style> 